/**
 * Prompt context types matching the backend PromptContextType enum.
 */
export type PromptContextType =
	| 'onboarding'
	| 'bi_dashboard'
	| 'bi_metric'
	| 'new_bi_metric'
	| 'embedded_prompt';

/**
 * Result type for prompt context determination.
 */
export type PromptContextResult = {
	contextType: PromptContextType | null;
	elementId: string | undefined;
};

/**
 * Extracts the ID from a colon-separated identifier.
 *
 * @param identifier - The identifier in format "type:id"
 * @returns The ID portion or undefined if not found
 */
export function extractIdFromIdentifier(
	identifier?: string | null
): string | undefined {
	return identifier?.split(':')[1];
}

/**
 * Determines the BI context type from a starting point entity identifier.
 * Used to understand what BI context the AI chat is operating in.
 *
 * @param identifier - The starting point entity identifier
 * @returns The BI context type or null if not a recognized BI context
 */
export function getBiContextType(
	identifier?: string | null
): 'new_bi_metric' | 'bi_metric' | 'bi_dashboard' | null {
	if (!identifier) {
		return null;
	}

	// New metric being created for a dashboard
	if (identifier.includes('new_bi_metric_for_bi_dashboard')) {
		return 'new_bi_metric';
	}

	// Existing metric - suggest modifications
	if (identifier.startsWith('bi_metric:')) {
		return 'bi_metric';
	}

	// Dashboard context
	if (identifier.startsWith('bi_dashboard:')) {
		return 'bi_dashboard';
	}

	return null;
}

/**
 * Determines the prompt context type and element ID.
 * Embedded prompts take precedence over BI contexts.
 *
 * @param initialId - The embedded prompt ID (if viewing an existing prompt)
 * @param startingPointEntityIdentifier - The starting point entity identifier for BI contexts
 * @returns Object containing the context type and element ID
 */
export function getPromptContext(
	initialId?: string,
	startingPointEntityIdentifier?: string | null
): PromptContextResult {
	// Embedded prompt takes precedence - if initialId exists, we're viewing an embedded prompt
	if (initialId) {
		return {
			contextType: 'embedded_prompt',
			elementId: initialId,
		};
	}

	// Fall back to BI contexts
	const biContextType = getBiContextType(startingPointEntityIdentifier);
	const elementId = extractIdFromIdentifier(startingPointEntityIdentifier);

	return {
		contextType: biContextType,
		elementId,
	};
}

/**
 * Determines if BI prompt suggestions should be shown based on context.
 *
 * @param identifier - The starting point entity identifier
 * @returns true if suggestions should be shown, false otherwise
 */
export function shouldShowPromptSuggestions(
	identifier?: string | null
): boolean {
	return getBiContextType(identifier) !== null;
}
